import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import Header from "common/page/Header";
import Content from "common/page/Content";
import Page from "common/page/Page";
import Accordion from "common/basicComponents/Accordion";
import Typography, { TypographyVariant } from "common/basicComponents/Typography";
import {
  addNotification,
  NotificationVariant,
} from "components/notifications/NotificationSlice";
import { formatDateDMY } from "utils/DateTimeUtils";
import ElementsProvider from "./element/ElementsProvider";
import CardListItem from "./cards/CardListItem";
import EditBillingDialog from "./cards/EditBillingDialog";
import { loadBilling, setActiveCard, deleteCard } from "./BillingActions";

const ListContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 1.25rem;
`;

const formatDate = formatDateDMY;

const Billing = () => {
  const { t } = useTranslation("billing");
  const dispatch = useDispatch();
  const { setup, cards, lastPayment } = useSelector((state) => state.billing);
  const [editingCard, setEditingCard] = useState(null);

  useEffect(() => {
    loadBilling();
  }, []);

  const onSetActive = async (id) => {
    const ok = await setActiveCard(id);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("common:form.savedSuccessfully"),
        })
      );
    }
  };

  const onDelete = async (card) => {
    const ok = await deleteCard(card.id);
    if (ok) {
      dispatch(
        addNotification({
          variant: NotificationVariant.SUCCESS,
          message: t("common:form.deletedSuccessfully"),
        })
      );
    }
  };

  return (
    <Page>
      <Header title={t("title")} />
      <Content>
        {lastPayment && (
          <Accordion text={t("lastPayment")} lockedOpen>
            <ListContent>
              <Typography
                variant={TypographyVariant.BODY1}
                text={`${(lastPayment.amount / 100).toFixed(2)} ${String(
                  lastPayment.currency
                ).toUpperCase()} — ${t(
                  `paymentStatus_${lastPayment.status}`,
                  { defaultValue: lastPayment.status }
                )} (${formatDate(lastPayment.date)})`}
              />
              {lastPayment.status === "failed" && (
                <Typography
                  variant={TypographyVariant.CAPTION}
                  text={t("updateCardHint")}
                />
              )}
            </ListContent>
          </Accordion>
        )}

        <Accordion text={t("cards")} lockedOpen>
          <ListContent>
            {cards && cards.length ? (
              cards.map((card) => (
                <CardListItem
                  key={card.id}
                  card={card}
                  onSetActive={onSetActive}
                  onEdit={setEditingCard}
                  onDelete={onDelete}
                  disableDelete={card.deletable === false}
                />
              ))
            ) : (
              <Typography
                variant={TypographyVariant.BODY1}
                text={t("noCards")}
              />
            )}
          </ListContent>
        </Accordion>

        <ElementsProvider setup={setup} />

        <EditBillingDialog
          open={!!editingCard}
          card={editingCard}
          onClose={() => setEditingCard(null)}
        />
      </Content>
    </Page>
  );
};

export default Billing;
